// Auth Context để quản lý trạng thái authentication
import React, { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import { AuthState, User, UserProfile, GoogleAuthResponse } from '../types/auth';
import { googleAuthService } from '../services/auth/googleAuthService';
import { userService } from '../services/database/userService';
import { roleService } from '../services/auth/RoleService';
import { seedDevUsers } from '../utils/seedDevUsers';
import { teacherStudentService } from '../services/teacher/TeacherStudentService';
import RoleSelectionModal from '../components/auth/RoleSelectionModal';
import { pythonAuthService } from '../services/api/PythonAuthService';
import { API_CONFIG } from '../config/api.config';

// Auth Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; profile: UserProfile; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'UPDATE_PROFILE'; payload: UserProfile };

// Initial state
const initialState: AuthState = {
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      // Lưu vào localStorage để persist qua page reload
      localStorage.setItem('is_authenticated', 'true');
      localStorage.setItem('auth_user', JSON.stringify(action.payload.user));
      if (action.payload.profile) {
        localStorage.setItem('user_profile', JSON.stringify(action.payload.profile));
      }

      // ✅ SYNC: Đảm bảo user cũng được lưu vào vatli365_users (cho getAllUsers())
      try {
        const existingUsers = localStorage.getItem('vatli365_users');
        const users = existingUsers ? JSON.parse(existingUsers) : [];
        const userIndex = users.findIndex((u: any) => u.id === action.payload.user.id || u.email === action.payload.user.email);

        if (userIndex >= 0) {
          // Update existing user
          users[userIndex] = { ...users[userIndex], ...action.payload.user, lastLoginAt: new Date() };
        } else {
          // Add new user
          users.push(action.payload.user);
        }

        localStorage.setItem('vatli365_users', JSON.stringify(users));
        console.log('✅ [AuthContext] Synced user to vatli365_users for getAllUsers()');
      } catch (error) {
        console.error('Failed to sync user to vatli365_users:', error);
      }

      return {
        ...state,
        user: action.payload.user,
        profile: action.payload.profile,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      // Xóa localStorage khi auth fail
      localStorage.removeItem('is_authenticated');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('user_profile');
      return {
        ...state,
        user: null,
        profile: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      // Xóa localStorage khi logout
      localStorage.removeItem('is_authenticated');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('user_profile');
      return {
        ...state,
        user: null,
        profile: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'UPDATE_PROFILE':
      return {
        ...state,
        profile: action.payload,
      };
    default:
      return state;
  }
};

// Auth Context
interface AuthContextType extends AuthState {
  loginWithGoogle: (credentialResponse?: any) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  refreshUser: () => Promise<void>;
  checkStudentInfo: () => Promise<boolean>; // ✅ Check and show modal if student needs to fill info (refreshes from DB)
  checkUserInfo: () => Promise<boolean>; // ✅ Check and show modal if user (student/teacher) needs to fill info
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const linkStudentToTeacherLists = async (user?: User | null) => {
    if (!user || user.role !== 'student') return;
    try {
      await teacherStudentService.claimRecords();
      console.log('👩‍🏫 Đồng bộ danh sách giáo viên cho học sinh thành công');
    } catch (error) {
      console.warn('Không thể đồng bộ danh sách giáo viên của học sinh:', error);
    }
  };

  // ✅ Helper function: Check if user needs to select role and fill info
  const needsRoleSelection = (user: User | null): boolean => {
    if (!user) return false;

    // Admin không cần chọn role
    if (user.role === 'admin') return false;

    // Student chưa có studentInfo → cần điền thông tin
    if (user.role === 'student' && !user.studentInfo) return true;

    // Teacher chưa có teacherInfo → cần điền thông tin
    if (user.role === 'teacher' && !user.teacherInfo) return true;

    return false;
  };

  // Initialize auth on mount
  useEffect(() => {
    // 🌱 Seed mock users cho dev mode (trước khi init auth)
    seedDevUsers();

    // Register callback so renderButton flow dispatches AUTH_SUCCESS
    googleAuthService.setLoginCallback(
      (authResponse) => {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: authResponse.user,
            profile: authResponse.profile,
            token: authResponse.token,
          },
        });
      },
      (error) => {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Đăng nhập thất bại: ' + error });
      }
    );

    // 🚀 Preload Google Auth script to avoid delay on click
    googleAuthService.initialize().catch(console.error);

    initializeAuth();
  }, []);

  // Decode JWT token
  const decodeJWT = (token: string): any => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      throw new Error('Invalid token');
    }
  };

  // Initialize authentication
  const initializeAuth = async () => {
    try {
      dispatch({ type: 'AUTH_START' });

      // DISABLED: Initialize SQLite database first
      // try {
      //   await userService.initialize();
      // } catch (error) {
      //   console.warn('Failed to initialize SQLite database, continuing without it:', error);
      // }
      console.log('Database initialization disabled');

      // ✅ DEV MODE: Auto-login without Google authentication
      if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true') {
        console.log('🔧 [Dev Mode] Auto-login enabled');

        const devUserEmail = import.meta.env.VITE_DEV_USER_EMAIL || 'dev@vatli365.vn';
        const devUserRole = import.meta.env.VITE_DEV_USER_ROLE || 'teacher';
        const devUserId = `dev_${devUserRole}_${Date.now()}`;

        const devUser: User = {
          id: devUserId,
          email: devUserEmail,
          name: `Dev ${devUserRole.charAt(0).toUpperCase() + devUserRole.slice(1)}`,
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + devUserEmail,
          provider: 'dev',
          providerId: devUserId,
          role: devUserRole as any,
          permissions: roleService.getRolePermissions(devUserRole as any),
          isActive: true,
          isVerified: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          // Add required info based on role
          ...(devUserRole === 'teacher' && {
            teacherInfo: {
              teacherId: devUserId,
              phone: '0123456789',
              department: 'Physics',
              subjects: ['Vật Lý 10', 'Vật Lý 11', 'Vật Lý 12'],
              experience: 5,
              qualifications: ['Đại học Sư phạm'],
              isApproved: true,
              approvedAt: new Date(),
              approvedBy: 'admin'
            }
          }),
          ...(devUserRole === 'student' && {
            studentInfo: {
              studentId: devUserId,
              class: '10A1',
              school: 'THPT Test',
              parentPhone: '0987654321'
            }
          })
        };

        const devProfile: UserProfile = {
          id: devUserId,
          userId: devUserId,
          fullName: devUser.name,
          bio: 'Development mode user',
          preferences: {
            theme: 'light',
            language: 'vi',
            notifications: true
          },
          createdAt: new Date(),
          updatedAt: new Date()
        };

        // Save to localStorage to persist across reloads
        localStorage.setItem('auth_token', 'dev_token_' + devUserId);
        localStorage.setItem('user_id', devUserId);
        localStorage.setItem('is_authenticated', 'true');

        console.log('✅ [Dev Mode] Auto-login successful:', {
          email: devUser.email,
          role: devUser.role,
          name: devUser.name
        });

        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: devUser,
            profile: devProfile,
            token: 'dev_token_' + devUserId,
          },
        });

        return; // Skip normal authentication flow
      }

      // Check if user is already authenticated
      const isAuth = googleAuthService.isAuthenticated();
      const userId = googleAuthService.getCurrentUserId();
      const token = googleAuthService.getCurrentToken();

      // Thử restore từ localStorage trước
      const storedUser = localStorage.getItem('auth_user');
      const storedProfile = localStorage.getItem('user_profile');

      console.log('🔍 AuthContext - Initialize auth check:', {
        isAuthenticated: isAuth,
        userId,
        hasToken: !!token,
        localStorage: {
          auth_token: localStorage.getItem('auth_token'),
          user_id: localStorage.getItem('user_id'),
          has_auth_user: !!storedUser,
          has_user_profile: !!storedProfile
        }
      });

      if (isAuth && userId) {
        console.log('🔍 AuthContext - User is authenticated, loading user data...');

        let user: User | null = null;
        let profile: UserProfile | null = null;

        // ✅ OPTIMIZATION: Show cached data immediately (optimistic UI)
        if (storedUser) {
          try {
            user = JSON.parse(storedUser);
            console.log('✅ [Optimistic UI] Showing cached user immediately:', user?.email);

            if (storedProfile) {
              try {
                profile = JSON.parse(storedProfile);
                console.log('✅ [Optimistic UI] Showing cached profile immediately');
              } catch (error) {
                console.error('Error parsing stored profile:', error);
              }
            }

            // ✅ FIX: Explicitly get token from localStorage to ensure API calls work on reload
            const restoredToken = token || localStorage.getItem('auth_token') || '';
            console.log('🔑 [Token Restore] Token available:', restoredToken ? 'Yes ✅' : 'No ❌');

            if (!restoredToken) {
              console.warn('⚠️ [Token Restore] No token found! API calls will fail!');
            }

            // ✅ Dispatch immediately with cached data AND restored token
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                user,
                profile: profile || null,
                token: restoredToken,
              },
            });

            // ✅ Then refresh in background (non-blocking and silent)
            console.log('🔄 [Background] Scheduling user data refresh...');

            // ✅ OPTIMIZATION: Use setTimeout to ensure truly non-blocking
            setTimeout(() => {
              userService.getUserById(userId).then(freshUser => {
                if (freshUser) {
                  console.log('✅ [Background] User data refreshed silently');

                  // ✅ Only dispatch if role or important data changed
                  if (freshUser.role !== user?.role ||
                    freshUser.permissions?.length !== user?.permissions?.length) {
                    console.log('📝 [Background] Important changes detected, updating state');
                    dispatch({ type: 'UPDATE_USER', payload: freshUser });
                  }

                  // Link to teacher lists if student
                  if (freshUser.role === 'student') {
                    linkStudentToTeacherLists(freshUser);
                  }
                } else {
                  console.warn('⚠️ [Background] User refresh failed. Keeping cached data.');
                }
              }).catch(error => {
                console.warn('⚠️ [Background] Failed to refresh user data:', error);
              });
            }, 100); // Small delay to ensure UI renders first

            return; // Exit early with cached data
          } catch (error) {
            console.error('Error parsing stored user:', error);
            // Fall through to fetch from API
          }
        }

        // 2. No cached data - fetch from API (blocking)
        console.log('⚠️ No cached data, fetching from API...');
        user = await userService.getUserById(userId);
        profile = await userService.getUserProfile(userId);

        // 🔧 DEV MODE: Tự động set role teacher cho email giáo viên trong dev
        if (user && import.meta.env.DEV) {
          const teacherEmails = ['tranqquants82@gmail.com', 'tranqquan1982@gmail.com'];
          if (teacherEmails.includes(user.email?.toLowerCase() || '')) {
            if (user.role !== 'teacher') {
              console.log('🔧 [Dev Mode] Auto-setting role to teacher for:', user.email);
              user = {
                ...user,
                role: 'teacher',
                permissions: roleService.getRolePermissions('teacher'),
              };
              // Update in database if possible
              try {
                await userService.updateUser(user.id, { role: 'teacher', permissions: user.permissions });
                console.log('✅ [Dev Mode] Updated user role in database');
              } catch (error) {
                console.warn('⚠️ [Dev Mode] Could not update role in database:', error);
              }
            }
          }
        }

        // If database is disabled and user is null, create user from Google OAuth data
        if (!user) {
          console.log('🔍 AuthContext - Database disabled, creating user from Google OAuth data...');

          // Decode JWT token to get user data
          if (token) {
            try {
              const payload = decodeJWT(token);
              console.log('🔍 AuthContext - Decoded JWT payload:', payload);

              // 🔧 DEV MODE: Tự động set role teacher cho email giáo viên trong dev
              const teacherEmails = ['tranqquants82@gmail.com', 'tranqquan1982@gmail.com'];
              const isTeacherEmail = teacherEmails.includes((payload.email || '').toLowerCase());
              const defaultRole = isTeacherEmail && import.meta.env.DEV ? 'teacher' : 'student';

              user = {
                id: userId,
                email: payload.email || '',
                name: payload.name || '',
                avatar: payload.picture || '',
                provider: 'google',
                providerId: payload.sub || '',
                role: defaultRole,
                permissions: roleService.getRolePermissions(defaultRole),
                isActive: true,
                isVerified: payload.email_verified || false,
                createdAt: new Date(),
                updatedAt: new Date()
              };

              if (isTeacherEmail && import.meta.env.DEV) {
                console.log('🔧 [Dev Mode] Auto-setting role to teacher for new user:', user.email);
              }

              // Create a basic profile
              profile = {
                id: userId,
                userId: userId,
                fullName: user.name,
                bio: '',
                preferences: {
                  theme: 'light',
                  language: 'vi',
                  notifications: true
                },
                createdAt: new Date(),
                updatedAt: new Date()
              };

              console.log('🔍 AuthContext - Created user from JWT token:', {
                id: user.id,
                name: user.name,
                email: user.email
              });
            } catch (error) {
              console.error('Error decoding JWT token:', error);
            }
          }
        }

        console.log('🔍 AuthContext - Loaded user data:', {
          user: user ? { id: user.id, name: user.name, email: user.email } : null,
          profile: profile ? { id: profile.id, fullName: profile.fullName } : null
        });

        if (user) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user,
              profile: profile || null,
              token: token || '',
            },
          });

          // ✅ Link to teacher lists if user is a student (no popup on login)
          if (user.role === 'student') {
            linkStudentToTeacherLists(user);
          }

          console.log('✅ AuthContext - User authenticated successfully');
        } else {
          console.log('❌ AuthContext - User not found, clearing auth');
          // User not found, clear auth
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } else {
        console.log('❌ AuthContext - User not authenticated, logging out');
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    } catch (error) {
      console.error('❌ AuthContext - Error initializing auth:', error);
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Login with Google
  const loginWithGoogle = async (credentialResponse?: any): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      // ✅ Refactored: Always use the consolidated googleAuthService
      // This service now intelligently handles both frontend Google interaction and Backend verification (via authApi)

      console.log('📝 Authenticating via GoogleAuthService (Consolidated)');

      // Initialize Google OAuth if not already done
      await googleAuthService.initialize();

      // Check if we have a real Google Client ID
      if (!import.meta.env.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID === 'test-client-id') {
        // ... (mock logic handled inside googleAuthService if needed, or we rely on it failing gracefully)
      }

      // ✅ EXISTING: WordPress backend flow (unchanged)
      // Proceed with Google Login validation

      // Check if we have a real Google Client ID
      if (!import.meta.env.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID === 'test-client-id') {
        console.warn('Google Client ID not configured. Using mock login.');
        const authResponse: GoogleAuthResponse = await googleAuthService.handleCredentialResponse({});

        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: authResponse.user,
            profile: authResponse.profile,
            token: authResponse.token,
          },
        });
        linkStudentToTeacherLists(authResponse.user);

        return;
      }

      // Handle real Google OAuth with credential response
      if (credentialResponse) {
        console.log('Processing real Google OAuth credential response in AuthContext');
        const authResponse: GoogleAuthResponse = await googleAuthService.handleCredentialResponse(credentialResponse);

        // googleAuthService.handleCredentialResponse đã tạo/update user rồi, không cần làm lại
        // Chỉ cần dispatch AUTH_SUCCESS
        console.log('🔍 AuthContext - Dispatching AUTH_SUCCESS with:', {
          user: authResponse.user ? { id: authResponse.user.id, name: authResponse.user.name, email: authResponse.user.email } : null,
          profile: authResponse.profile ? { id: authResponse.profile.id, fullName: authResponse.profile.fullName } : null,
          hasToken: !!authResponse.token
        });

        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: authResponse.user,
            profile: authResponse.profile,
            token: authResponse.token,
          },
        });

        console.log('✅ AuthContext - AUTH_SUCCESS dispatched');

        // ✅ CRITICAL FIX: Persist teacher auto-approval to database
        if (authResponse.user.role === 'teacher' && authResponse.user.teacherInfo?.isApproved) {
          // Check if this was auto-fixed (not from backend)
          // If teacherInfo was just created/fixed, update database
          console.log('🔧 Persisting teacher auto-approval to database...');
          try {
            await userService.updateUser(authResponse.user.id, {
              teacherInfo: authResponse.user.teacherInfo
            });
            console.log('✅ Teacher approval status persisted');
          } catch (error) {
            console.warn('⚠️ Failed to persist teacher approval, will retry on next action:', error);
          }
        }

        // ✅ Link to teacher lists if user is a student (no popup on login)
        if (authResponse.user.role === 'student') {
          linkStudentToTeacherLists(authResponse.user);
        }

        return;
      }

      // Use Google Sign-In Button approach (fallback)
      if (window.google && window.google.accounts) {
        // Google OAuth is available, callback will be handled by GoogleSignInButton component
        console.log('Google OAuth initialized, using existing GoogleSignInButton component');
      } else {
        throw new Error('Google OAuth not available');
      }
    } catch (error) {
      console.error('Error logging in with Google:', error);
      dispatch({ type: 'AUTH_FAILURE', payload: 'Failed to login with Google' });
      throw error;
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await googleAuthService.signOut();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  // Update user
  const updateUser = async (userData: Partial<User>): Promise<void> => {
    try {
      if (!state.user) throw new Error('No user logged in');

      const updatedUser = await userService.updateUser(state.user.id, userData);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  // Update profile
  const updateProfile = async (profileData: Partial<UserProfile>): Promise<void> => {
    try {
      if (!state.user) throw new Error('No user logged in');

      const updatedProfile = await userService.updateUserProfile(state.user.id, profileData);
      dispatch({ type: 'UPDATE_PROFILE', payload: updatedProfile });
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  // Refresh user data
  const refreshUser = async (): Promise<void> => {
    try {
      if (!state.user) return;

      const user = await userService.getUserById(state.user.id);
      const profile = await userService.getUserProfile(state.user.id);

      if (user) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user,
            profile: profile || null,
            token: googleAuthService.getCurrentToken() || '',
          },
        });
        linkStudentToTeacherLists(user);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  // Handle role selection modal completion
  const handleRoleSelectionComplete = async (updatedUser: User) => {
    setShowRoleModal(false);

    // Update state with new user info
    dispatch({
      type: 'UPDATE_USER',
      payload: updatedUser,
    });

    // Refresh user to get latest data
    await refreshUser();

    // Link to teacher lists if student
    if (updatedUser.role === 'student') {
      await linkStudentToTeacherLists(updatedUser);
    }
  };

  const handleRoleSelectionClose = () => {
    // Don't allow closing without completing (user must fill info)
    // But we can allow it for now, user can complete later
    setShowRoleModal(false);
  };

  // ✅ Check if student needs to fill info and show modal if needed
  // ✅ IMPORTANT: Refresh user from WordPress API before checking to ensure latest data
  const checkStudentInfo = async (): Promise<boolean> => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 [checkStudentInfo] Starting check...');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📊 Current state:', {
      hasUser: !!state.user,
      userRole: state.user?.role,
      userId: state.user?.id,
      userEmail: state.user?.email
    });

    if (!state.user) {
      console.log('❌ [checkStudentInfo] No user found');
      return false; // No user, no need to check
    }

    // ✅ [TẠM VÔ HIỆU HÓA] Refresh user from WordPress API to get latest studentInfo
    // ⚠️ ĐÃ VÔ HIỆU HÓA TẠM THỜI để tránh treo do WordPress API issues
    // 🔧 Để bật lại: Uncomment phần code bên dưới
    let currentUser = state.user;

    /* [TẠM VÔ HIỆU HÓA - VÔ HIỆU HÓA KIỂM TRA DATABASE TỪ SERVER]
    try {
      console.log('🔄 [checkStudentInfo] Refreshing user from WordPress API...');
      console.log('🆔 User ID:', state.user.id);

      // ✅ FIX: Thêm timeout 5 giây để tránh treo quá lâu
      const freshUser = await Promise.race([
        userService.getUserById(state.user.id),
        new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('getUserById timeout after 5s')), 5000)
        )
      ]).catch(err => {
        if (err.message?.includes('timeout')) {
          console.warn('⚠️ [checkStudentInfo] API call timeout, using cached data');
          return null;
        }
        throw err;
      });

      if (freshUser) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ [checkStudentInfo] User refreshed from API');
        console.log('📊 Fresh user data:', {
          id: freshUser.id,
          email: freshUser.email,
          role: freshUser.role,
          hasStudentInfo: !!freshUser.studentInfo,
          studentInfo: freshUser.studentInfo,
          studentInfoType: typeof freshUser.studentInfo
        });
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // Use fresh user data for checking
        currentUser = freshUser;

        // Update state with fresh user data
        dispatch({
          type: 'UPDATE_USER',
          payload: freshUser,
        });
      } else {
        console.warn('⚠️ [checkStudentInfo] Fresh user is null, using cached data');
      }
    } catch (error) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ [checkStudentInfo] Error refreshing user from API:', error);
      console.error('📊 Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      console.error('⚠️ Using cached user data instead');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // Continue with cached data if refresh fails
    }
    */

    // ✅ Sử dụng cached user data thay vì refresh từ server
    console.log('ℹ️ [checkStudentInfo] Using cached user data (server check disabled)');

    // If user doesn't have a role yet, show modal to select role
    // ✅ TẠM ẨN: Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau
    if (!currentUser.role || (currentUser.role !== 'student' && currentUser.role !== 'teacher' && currentUser.role !== 'admin')) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('⚠️ [checkStudentInfo] User has no valid role');
      console.log('📊 Current role:', currentUser.role);
      console.log('🎯 [TẠM ẨN] Registration modal disabled');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // setShowRoleModal(true); // ✅ TẠM ẨN
      return false; // ✅ TẠM ẨN: Không block user
    }

    // Only check student info if user is a student
    if (currentUser.role !== 'student') {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('ℹ️ [checkStudentInfo] User is not a student');
      console.log('📊 Role:', currentUser.role);
      console.log('✅ No check needed');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return false; // Not a student, no need to check
    }

    // Check if student has complete info (class, school, phone)
    const studentInfo = currentUser.studentInfo;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 [checkStudentInfo] Checking student info...');
    console.log('📊 Student info:', {
      hasStudentInfo: !!studentInfo,
      studentInfoType: typeof studentInfo,
      studentInfoValue: studentInfo
    });
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    if (!studentInfo) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('⚠️ [checkStudentInfo] Student has no studentInfo');
      console.log('🎯 [TẠM ẨN] Registration modal disabled');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // setShowRoleModal(true); // ✅ TẠM ẨN
      return false; // ✅ TẠM ẨN: Không block user
    }

    // Validate studentInfo is an object
    if (typeof studentInfo !== 'object' || Array.isArray(studentInfo)) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('⚠️ [checkStudentInfo] studentInfo is not a valid object');
      console.log('📊 Type:', typeof studentInfo);
      console.log('📊 Value:', studentInfo);
      console.log('🎯 [TẠM ẨN] Registration modal disabled');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // setShowRoleModal(true); // ✅ TẠM ẨN
      return false; // ✅ TẠM ẨN: Không block user
    }

    const hasClass = studentInfo.class && typeof studentInfo.class === 'string' && studentInfo.class.trim() !== '';
    const hasSchool = studentInfo.school && typeof studentInfo.school === 'string' && studentInfo.school.trim() !== '';
    const hasPhone = studentInfo.parentPhone && typeof studentInfo.parentPhone === 'string' && studentInfo.parentPhone.trim() !== '';

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 [checkStudentInfo] Checking required fields:');
    console.log('📊 Field validation:', {
      hasClass,
      hasSchool,
      hasPhone,
      class: studentInfo.class,
      school: studentInfo.school,
      phone: studentInfo.parentPhone
    });
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    if (!hasClass || !hasSchool || !hasPhone) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('⚠️ [checkStudentInfo] Student missing required info');
      console.log('📊 Missing fields:', {
        class: !hasClass,
        school: !hasSchool,
        phone: !hasPhone
      });
      console.log('🎯 [TẠM ẨN] Registration modal disabled');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // setShowRoleModal(true); // ✅ TẠM ẨN
      return false; // ✅ TẠM ẨN: Không block user
    }

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('✅ [checkStudentInfo] Student has complete info');
    console.log('📊 All fields validated:', {
      class: hasClass,
      school: hasSchool,
      phone: hasPhone
    });
    console.log('✅ No modal needed');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    return false; // All info complete, no modal needed
  };

  // ✅ Check if user (student/teacher) needs to fill info and show modal if needed
  // ✅ IMPORTANT: Refresh user from WordPress API before checking to ensure latest data
  // ✅ For students: Check class, school, parentPhone
  // ✅ For teachers: Check phone, department, subjects
  const checkUserInfo = async (): Promise<boolean> => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 [checkUserInfo] Starting check...');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📊 Current state:', {
      hasUser: !!state.user,
      userRole: state.user?.role,
      userId: state.user?.id,
      userEmail: state.user?.email
    });

    if (!state.user) {
      console.log('❌ [checkUserInfo] No user found - user needs to login');
      return true; // No user, need to login
    }

    // ✅ [TẠM VÔ HIỆU HÓA] Refresh user from WordPress API to get latest info
    // ⚠️ ĐÃ VÔ HIỆU HÓA TẠM THỜI để tránh treo do WordPress API issues
    // 🔧 Để bật lại: Uncomment phần code bên dưới
    let currentUser = state.user;

    /* [TẠM VÔ HIỆU HÓA - VÔ HIỆU HÓA KIỂM TRA DATABASE TỪ SERVER]
    try {
      console.log('🔄 [checkUserInfo] Refreshing user from WordPress API...');
      console.log('🆔 User ID:', state.user.id);
      console.log('📧 User Email:', state.user.email);

      // ✅ FIX: Thêm timeout 5 giây để tránh treo quá lâu
      // Try to get user by ID first
      let freshUser = await Promise.race([
        userService.getUserById(state.user.id),
        new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('getUserById timeout after 5s')), 5000)
        )
      ]).catch(err => {
        if (err.message?.includes('timeout')) {
          console.warn('⚠️ [checkUserInfo] getUserById timeout, trying by email...');
          return null;
        }
        throw err;
      });

      // If not found by ID, try by email (fallback)
      if (!freshUser && state.user.email) {
        console.log('⚠️ [checkUserInfo] User not found by ID, trying by email...');
        freshUser = await Promise.race([
          userService.getUserByEmail(state.user.email),
          new Promise<null>((_, reject) => 
            setTimeout(() => reject(new Error('getUserByEmail timeout after 5s')), 5000)
          )
        ]).catch(err => {
          if (err.message?.includes('timeout')) {
            console.warn('⚠️ [checkUserInfo] getUserByEmail timeout, using cached data');
            return null;
          }
          throw err;
        });
      }

      if (freshUser) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('✅ [checkUserInfo] User refreshed from API');
        console.log('📊 Fresh user data:', {
          id: freshUser.id,
          email: freshUser.email,
          role: freshUser.role,
          hasStudentInfo: !!freshUser.studentInfo,
          hasTeacherInfo: !!freshUser.teacherInfo,
          studentInfo: freshUser.studentInfo,
          teacherInfo: freshUser.teacherInfo
        });
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        currentUser = freshUser;

        dispatch({
          type: 'UPDATE_USER',
          payload: freshUser,
        });
      } else {
        console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.warn('⚠️ [checkUserInfo] User not found in database');
        console.warn('📊 This means user needs to register or login again');
        console.warn('🎯 Will show registration modal');
        console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // User không tồn tại trong database -> cần đăng ký lại
        // ✅ TẠM ẨN: Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau
        // setShowRoleModal(true); // ✅ TẠM ẨN
        console.log('🎯 [TẠM ẨN] Registration modal disabled');
        return false; // ✅ TẠM ẨN: Không block user
      }
    } catch (error) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ [checkUserInfo] Error refreshing user from API:', error);
      console.error('⚠️ Using cached user data instead');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Nếu có lỗi khi gọi API, vẫn kiểm tra với cached data
      // Nhưng nếu là lỗi network hoặc API không khả dụng, có thể cần xử lý khác
    }
    */

    // ✅ Sử dụng cached user data thay vì refresh từ server
    console.log('ℹ️ [checkUserInfo] Using cached user data (server check disabled)');

    // If user doesn't have a role yet, show modal to select role
    // ✅ TẠM ẨN: Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau
    if (!currentUser.role || (currentUser.role !== 'student' && currentUser.role !== 'teacher' && currentUser.role !== 'admin')) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('⚠️ [checkUserInfo] User has no valid role');
      console.log('🎯 [TẠM ẨN] Registration modal disabled');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // setShowRoleModal(true); // ✅ TẠM ẨN
      return false; // ✅ TẠM ẨN: Không block user
    }

    // ✅ FIX: Chỉ kiểm tra thông tin cho học sinh, và chỉ hiện popup khi thực sự thiếu thông tin
    if (currentUser.role === 'student') {
      const studentInfo = currentUser.studentInfo;

      // ✅ FIX: Kiểm tra kỹ hơn - chỉ hiện popup khi thực sự thiếu thông tin
      // Nếu studentInfo không tồn tại hoặc không phải object hợp lệ -> cần đăng ký
      // ✅ TẠM ẨN: Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau
      if (!studentInfo || typeof studentInfo !== 'object' || Array.isArray(studentInfo)) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('⚠️ [checkUserInfo] Student has no valid studentInfo');
        console.log('📊 studentInfo:', studentInfo);
        console.log('📊 Type:', typeof studentInfo);
        console.log('🎯 [TẠM ẨN] Registration modal disabled');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // setShowRoleModal(true); // ✅ TẠM ẨN
        return false; // ✅ TẠM ẨN: Không block user
      }

      // ✅ FIX: Kiểm tra nếu studentInfo là object rỗng -> cần đăng ký
      // ✅ TẠM ẨN: Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau
      if (Object.keys(studentInfo).length === 0) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('⚠️ [checkUserInfo] Student has empty studentInfo object');
        console.log('🎯 [TẠM ẨN] Registration modal disabled');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // setShowRoleModal(true); // ✅ TẠM ẨN
        return false; // ✅ TẠM ẨN: Không block user
      }

      // ✅ FIX: Validate required fields - chỉ hiện popup khi THIẾU thông tin
      // Kiểm tra từng field một cách chính xác
      const classValue = studentInfo.class;
      const schoolValue = studentInfo.school;
      const phoneValue = studentInfo.parentPhone;

      // ✅ FIX: Kiểm tra kỹ hơn - đảm bảo giá trị không chỉ là string rỗng hoặc null/undefined
      const hasClass = classValue &&
        (typeof classValue === 'string' ? classValue.trim() !== '' :
          typeof classValue === 'number' ? classValue > 0 : false);

      const hasSchool = schoolValue &&
        (typeof schoolValue === 'string' ? schoolValue.trim() !== '' : false);

      const hasPhone = phoneValue &&
        (typeof phoneValue === 'string' ? phoneValue.trim() !== '' : false);

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🔍 [checkUserInfo] Student field validation:');
      console.log('📊 Raw fields:', {
        class: classValue,
        school: schoolValue,
        parentPhone: phoneValue ? '***' : null
      });
      console.log('📊 Validation result:', {
        hasClass,
        hasSchool,
        hasPhone,
        classType: typeof classValue,
        schoolType: typeof schoolValue,
        phoneType: typeof phoneValue
      });
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // ✅ FIX: Chỉ hiện popup khi THIẾU ít nhất một field bắt buộc
      // ✅ TẠM ẨN: Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau
      if (!hasClass || !hasSchool || !hasPhone) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('⚠️ [checkUserInfo] Student missing required info');
        console.log('📊 Missing fields:', {
          class: !hasClass,
          school: !hasSchool,
          phone: !hasPhone
        });
        console.log('🎯 [TẠM ẨN] Registration modal disabled');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        // setShowRoleModal(true); // ✅ TẠM ẨN
        return false; // ✅ TẠM ẨN: Không block user
      }

      // ✅ FIX: Nếu có đầy đủ thông tin, không hiện popup
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ [checkUserInfo] Student has complete info');
      console.log('📊 All fields validated:', {
        class: hasClass,
        school: hasSchool,
        phone: hasPhone
      });
      console.log('✅ No registration modal needed');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return false;
    }

    // ✅ FIX: Đối với giáo viên, không yêu cầu bổ sung thông tin
    if (currentUser.role === 'teacher') {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('ℹ️ [checkUserInfo] User is teacher');
      console.log('✅ No registration required for teachers');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return false; // Không yêu cầu bổ sung thông tin cho giáo viên
    }

    // Admin doesn't need to check
    if (currentUser.role === 'admin') {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('ℹ️ [checkUserInfo] User is admin');
      console.log('✅ No check needed');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return false;
    }

    return false;
  };

  const value: AuthContextType = {
    ...state,
    loginWithGoogle,
    logout,
    updateUser,
    updateProfile,
    refreshUser,
    checkStudentInfo,
    checkUserInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* ✅ TẠM ẨN: Role Selection Modal - Đã tắt popup bổ sung thông tin, sẽ kích hoạt sau */}
      {/* {showRoleModal && state.user && (
        <>
          {console.log('🎯 [AuthProvider] Rendering RoleSelectionModal', {
            showRoleModal,
            hasUser: !!state.user,
            userRole: state.user.role,
            userEmail: state.user.email
          })}
          <RoleSelectionModal
            user={state.user}
            onComplete={handleRoleSelectionComplete}
            onClose={handleRoleSelectionClose}
          />
        </>
      )} */}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
let hasWarnedAboutOutsideProvider = false; // Chỉ log warning một lần

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return a fallback context to prevent crashes during hot reload
    // Chỉ log warning một lần để tránh spam console trong dev mode
    if (!hasWarnedAboutOutsideProvider) {
      console.warn('useAuth called outside AuthProvider, returning fallback context');
      hasWarnedAboutOutsideProvider = true;
      // Reset sau 5 giây để có thể log lại nếu cần
      setTimeout(() => {
        hasWarnedAboutOutsideProvider = false;
      }, 5000);
    }
    return {
      user: null,
      profile: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      loginWithGoogle: async () => {
        // Không log để tránh spam
      },
      logout: async () => {
        // Không log để tránh spam
      },
      updateUser: async () => {
        // Không log để tránh spam
      },
      updateProfile: async () => {
        // Không log để tránh spam
      },
      refreshUser: async () => {
        // Không log để tránh spam
      },
      checkStudentInfo: async () => {
        return false;
      },
      checkUserInfo: async () => {
        return false;
      },
    };
  }
  return context;
};
